import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/** Buyer Categories hub: keep always starting at top (see Categories.js). */
function isCategoriesRoute(pathname) {
    return pathname === '/categories';
}

function scrollStorageKey(locationKey) {
    return `scrollPos:${locationKey}`;
}

/**
 * - PUSH/REPLACE: scroll to top so new screens start at the top.
 * - POP (back/forward): restore window scroll for this history entry (see location.key).
 * - Categories route: always align to top; do not persist/restore scroll here.
 */
const ScrollToTop = () => {
    const location = useLocation();
    const navigationType = useNavigationType();
    const { pathname, key: locationKey } = location;
    const debounceRef = useRef(null);

    useEffect(() => {
        if (isCategoriesRoute(pathname)) {
            return undefined;
        }

        const storageKey = scrollStorageKey(locationKey);

        const save = () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                try {
                    sessionStorage.setItem(storageKey, String(window.scrollY));
                } catch {
                    /* ignore quota / private mode */
                }
            }, 120);
        };

        window.addEventListener('scroll', save, { passive: true });
        return () => {
            window.removeEventListener('scroll', save);
            if (debounceRef.current) clearTimeout(debounceRef.current);
            try {
                sessionStorage.setItem(storageKey, String(window.scrollY));
            } catch {
                /* ignore */
            }
        };
    }, [locationKey, pathname]);

    useLayoutEffect(() => {
        if (isCategoriesRoute(pathname)) {
            window.scrollTo(0, 0);
            return;
        }

        if (navigationType === 'POP') {
            let y = null;
            try {
                const raw = sessionStorage.getItem(scrollStorageKey(locationKey));
                if (raw != null) y = parseInt(raw, 10);
            } catch {
                /* ignore */
            }
            if (y != null && !Number.isNaN(y)) {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        window.scrollTo(0, y);
                    });
                });
            }
            return;
        }

        window.scrollTo(0, 0);
    }, [locationKey, pathname, navigationType]);

    return null;
};

export default ScrollToTop;
